import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Pill, Printer, FileText, Plus, Trash2, Edit, X, ChevronLeft, ChevronRight, Save, Calendar, Search } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (d) => { if (!d) return '-'; const p = d.slice(0, 10).split('-'); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : d; };
// IMPORTANT: use LOCAL date components, NOT toISOString() (which converts to UTC and
// causes a 1-day shift in timezones ahead of UTC, e.g. Portugal in DST = UTC+1).
const isoDay = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
// Parse a YYYY-MM-DD ISO date string as LOCAL midnight (avoids the UTC-shift bug
// that new Date("2026-08-19") introduces).
const parseIsoDate = (iso) => {
  if (!iso) return new Date();
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
};
const MESES = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];

// Week starts on Saturday (Portuguese CRO shelter convention as in the reference image).
// Given any date, return the Saturday <= that date.
function getSaturdayOfWeek(anyDate) {
  const d = new Date(anyDate);
  d.setHours(0, 0, 0, 0);
  const dow = d.getDay(); // 0 Sun, 6 Sat
  const diffToSat = (dow === 6) ? 0 : (dow + 1);
  d.setDate(d.getDate() - diffToSat);
  return d;
}

function DateInput({ value, onChange, className = '' }) {
  // Uses the native <input type="date"> for the calendar picker (great UX on all devices),
  // but overlays a fixed dd/mm/yyyy display so the format is consistent regardless of the
  // browser/OS locale (some browsers show mm/dd/yyyy by default which confuses PT users).
  const iso = value ? String(value).slice(0, 10) : '';
  const display = iso && iso.split('-').length === 3
    ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`
    : '';
  return (
    <div className={`relative rounded-lg border border-border bg-background focus-within:ring-1 focus-within:ring-primary ${className}`}>
      <input
        type="date"
        value={iso}
        onChange={e => onChange(e.target.value)}
        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        aria-label="Data"
      />
      <div className="px-2 py-1.5 text-sm flex items-center justify-between gap-2 pointer-events-none">
        <span className={display ? '' : 'text-muted-foreground'}>{display || 'dd/mm/aaaa'}</span>
        <Calendar className="w-4 h-4 text-muted-foreground flex-shrink-0" />
      </div>
    </div>
  );
}

const DAYS_META = [
  { key: 'sabado', label: 'SÁBADO' },
  { key: 'domingo', label: 'DOMINGO' },
  { key: 'segunda', label: 'SEGUNDA' },
  { key: 'terca', label: 'TERÇA' },
  { key: 'quarta', label: 'QUARTA' },
  { key: 'quinta', label: 'QUINTA' },
  { key: 'sexta', label: 'SEXTA' },
];

export default function TratamentosPage() {
  const [tratamentos, setTratamentos] = useState([]);
  const [protocolos, setProtocolos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddProt, setShowAddProt] = useState(false);
  const [newProt, setNewProt] = useState({ titulo: '', conteudo: '' });
  const [showAddTrat, setShowAddTrat] = useState(false);
  const [animais, setAnimais] = useState([]);
  const [newTrat, setNewTrat] = useState({ animal_id: '', nome: '', descricao: '', protocolo: '', data_inicio: '', data_fim: '', prescricoes_dia: {} });
  const [editing, setEditing] = useState(null); // { id, nome, descricao, protocolo, data_inicio, data_fim, prescricoes_dia }
  const [weekStart, setWeekStart] = useState(() => getSaturdayOfWeek(new Date()));
  const [animalQuery, setAnimalQuery] = useState('');
  const [animalOpen, setAnimalOpen] = useState(false);
  const [pagina, setPagina] = useState(0);
  const PER_PAGE = 15;

  const dias = useMemo(() => {
    return DAYS_META.map((m, i) => {
      const d = new Date(weekStart);
      d.setDate(d.getDate() + i);
      return { ...m, date: d, iso: isoDay(d), display: `${String(d.getDate()).padStart(2, '0')} DE ${MESES[d.getMonth()]}` };
    });
  }, [weekStart]);

  const animalSelecionado = useMemo(
    () => animais.find(a => a.id === newTrat.animal_id) || null,
    [animais, newTrat.animal_id]
  );

  const animaisFiltrados = useMemo(() => {
    const q = animalQuery.trim().toLowerCase();
    if (!q) return animais.slice(0, 30);
    return animais.filter(a =>
      (a.nome || '').toLowerCase().includes(q)
      || (a.chip || '').toLowerCase().includes(q)
      || (a.box || '').toLowerCase().includes(q)
    ).slice(0, 30);
  }, [animais, animalQuery]);

  const totalPaginas = Math.max(1, Math.ceil(tratamentos.length / PER_PAGE));
  const paginaAtual = Math.min(pagina, totalPaginas - 1);
  const tratamentosVisiveis = tratamentos.slice(paginaAtual * PER_PAGE, (paginaAtual + 1) * PER_PAGE);

  const carregar = () => {
    const ws = isoDay(weekStart);
    Promise.all([
      axios.get(`${API}/tratamentos/semanais?week_start=${ws}`),
      axios.get(`${API}/protocolos`),
      axios.get(`${API}/animais?ativos=true`)
    ]).then(([t, p, a]) => {
      setTratamentos(t.data); setProtocolos(p.data); setAnimais(a.data);
    }).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => { carregar(); }, [weekStart]);

  const addProtocolo = async () => {
    if (!newProt.titulo) return;
    await axios.post(`${API}/protocolos`, newProt);
    setNewProt({ titulo: '', conteudo: '' }); setShowAddProt(false); carregar();
  };

  const deleteProtocolo = async (id) => {
    if (!window.confirm('Apagar este protocolo?')) return;
    await axios.delete(`${API}/protocolos/${id}`); carregar();
  };

  const addTratamento = async () => {
    if (!newTrat.animal_id || !newTrat.nome) return;
    await axios.post(`${API}/animais/${newTrat.animal_id}/tratamentos`, newTrat);
    // Auto-navigate to the week that contains the treatment's start date so the user
    // actually sees what was just created (weekly grid is always shown for one week).
    if (newTrat.data_inicio) {
      setWeekStart(getSaturdayOfWeek(parseIsoDate(newTrat.data_inicio)));
    }
    setNewTrat({ animal_id: '', nome: '', descricao: '', protocolo: '', data_inicio: '', data_fim: '', prescricoes_dia: {} });
    setShowAddTrat(false); carregar();
  };

  const guardarEdit = async () => {
    if (!editing) return;
    const payload = {
      nome: editing.nome,
      descricao: editing.descricao,
      protocolo: editing.protocolo,
      data_inicio: editing.data_inicio,
      data_fim: editing.data_fim,
      prescricoes_dia: editing.prescricoes_dia || {},
    };
    await axios.put(`${API}/tratamentos/${editing.id}`, payload);
    setEditing(null); carregar();
  };

  const apagarTratamento = async (t) => {
    if (!window.confirm(`Apagar o tratamento "${t.nome}"?`)) return;
    await axios.delete(`${API}/tratamentos/${t.id}`); carregar();
  };

  const exportPdf = () => {
    const ws = isoDay(weekStart);
    window.open(`${API}/tratamentos/pdf?week_start=${ws}`, '_blank');
  };

  // Cell content: prescricoes_dia[key] if set; else, if date in [data_inicio, data_fim] show descricao; else "X"
  const cellContent = (t, iso, dayKey) => {
    const p = t.prescricoes_dia || {};
    if (p[dayKey] !== undefined && p[dayKey] !== null) return p[dayKey] || '';
    const di = (t.data_inicio || '').slice(0, 10);
    const df = (t.data_fim || '').slice(0, 10);
    if (di && df && iso >= di && iso <= df) return t.descricao || '';
    if (di && !df && iso >= di) return t.descricao || '';
    return 'X';
  };

  const shiftWeek = (n) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + n * 7);
    setWeekStart(getSaturdayOfWeek(d));
  };

  if (loading) return <div className="text-center py-12 text-muted-foreground">A carregar...</div>;

  return (
    <div data-testid="tratamentos-page">
      <div className="flex items-center justify-between mb-4 print:mb-2 flex-wrap gap-2">
        <h2 className="font-heading font-bold text-2xl flex items-center gap-2"><Pill className="w-6 h-6 text-primary" /> Registo de Tratamentos</h2>
        <div className="flex gap-2 print:hidden flex-wrap">
          <button onClick={() => setShowAddTrat(!showAddTrat)} data-testid="btn-novo-tratamento"
            className="flex items-center gap-1 bg-primary text-primary-foreground rounded-full px-4 py-2 text-sm font-medium hover:brightness-110">
            <Plus className="w-4 h-4" /> Novo Tratamento
          </button>
          <button onClick={exportPdf} data-testid="pdf-tratamentos"
            className="flex items-center gap-1 bg-accent text-accent-foreground rounded-full px-4 py-2 text-sm font-medium hover:brightness-110">
            <FileText className="w-4 h-4" /> Exportar PDF
          </button>
          <button onClick={() => window.print()} className="flex items-center gap-1 bg-muted text-foreground rounded-full px-4 py-2 text-sm font-medium hover:bg-border">
            <Printer className="w-4 h-4" /> Imprimir
          </button>
        </div>
      </div>

      {/* Week navigator */}
      <div className="flex items-center justify-center gap-4 mb-4 print:mb-2">
        <button onClick={() => shiftWeek(-1)} data-testid="week-prev"
          className="p-2 rounded-full bg-muted hover:bg-border"><ChevronLeft className="w-4 h-4" /></button>
        <div className="text-sm font-medium">
          Semana de {fmtDate(isoDay(dias[0].date))} a {fmtDate(isoDay(dias[6].date))}
        </div>
        <button onClick={() => shiftWeek(1)} data-testid="week-next"
          className="p-2 rounded-full bg-muted hover:bg-border"><ChevronRight className="w-4 h-4" /></button>
        <button onClick={() => setWeekStart(getSaturdayOfWeek(new Date()))}
          className="text-xs bg-primary/10 text-primary rounded-full px-3 py-1.5">Semana atual</button>
      </div>

      {/* Add treatment form */}
      {showAddTrat && (
        <div className="bg-card rounded-2xl border border-border p-4 mb-6 space-y-3 print:hidden">
          <h3 className="font-heading font-bold text-lg">Adicionar Tratamento</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            <div><label className="text-xs block mb-1">Animal *</label>
              <div className="relative">
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
                  <input
                    data-testid="novo-trat-animal"
                    type="text"
                    value={animalSelecionado ? `${animalSelecionado.nome}${animalSelecionado.box ? ` (${animalSelecionado.box})` : ''}` : animalQuery}
                    onChange={e => { setAnimalQuery(e.target.value); setNewTrat({ ...newTrat, animal_id: '' }); setAnimalOpen(true); }}
                    onFocus={() => setAnimalOpen(true)}
                    onBlur={() => setTimeout(() => setAnimalOpen(false), 150)}
                    placeholder="Escrever nome, chip ou box…"
                    className="w-full rounded-lg border border-border bg-background pl-7 pr-8 py-1.5 text-sm" />
                  {(animalSelecionado || animalQuery) && (
                    <button type="button" onClick={() => { setNewTrat({ ...newTrat, animal_id: '' }); setAnimalQuery(''); setAnimalOpen(true); }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      aria-label="Limpar">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                {animalOpen && !animalSelecionado && animaisFiltrados.length > 0 && (
                  <div className="absolute z-50 mt-1 w-full max-h-64 overflow-y-auto bg-card border border-border rounded-lg shadow-lg" data-testid="novo-trat-animal-list">
                    {animaisFiltrados.map(a => (
                      <button
                        type="button"
                        key={a.id}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => { setNewTrat({ ...newTrat, animal_id: a.id }); setAnimalQuery(''); setAnimalOpen(false); }}
                        className="w-full text-left px-3 py-1.5 text-sm hover:bg-muted flex justify-between gap-2">
                        <span className="font-medium">{a.nome}</span>
                        <span className="text-xs text-muted-foreground">{a.especie || ''}{a.box ? ` · ${a.box}` : ''}</span>
                      </button>
                    ))}
                  </div>
                )}
                {animalOpen && !animalSelecionado && animalQuery && animaisFiltrados.length === 0 && (
                  <div className="absolute z-50 mt-1 w-full bg-card border border-border rounded-lg shadow-lg px-3 py-2 text-xs text-muted-foreground">
                    Sem correspondência para {'"'}{animalQuery}{'"'}
                  </div>
                )}
              </div>
            </div>
            <div><label className="text-xs block mb-1">Motivo / Nome *</label><input value={newTrat.nome} onChange={e => setNewTrat({ ...newTrat, nome: e.target.value })} placeholder="ex: Displasia, Otite crónica" className="w-full rounded-lg border border-border bg-background px-2 py-1.5 text-sm" /></div>
            <div><label className="text-xs block mb-1">Protocolo</label><input value={newTrat.protocolo} onChange={e => setNewTrat({ ...newTrat, protocolo: e.target.value })} className="w-full rounded-lg border border-border bg-background px-2 py-1.5 text-sm" /></div>
            <div><label className="text-xs block mb-1">Data Início</label><DateInput value={newTrat.data_inicio} onChange={v => setNewTrat({ ...newTrat, data_inicio: v })} className="w-full" /></div>
            <div><label className="text-xs block mb-1">Data Fim</label><DateInput value={newTrat.data_fim} onChange={v => setNewTrat({ ...newTrat, data_fim: v })} className="w-full" /></div>
            <div className="sm:col-span-2 md:col-span-3">
              <label className="text-xs block mb-1">Prescrição (aplicada em todos os dias dentro do intervalo — pode ser sobreposta por dia depois de guardar)</label>
              <input value={newTrat.descricao} onChange={e => setNewTrat({ ...newTrat, descricao: e.target.value })} placeholder="ex: 1 CP CONDROSATIVA" className="w-full rounded-lg border border-border bg-background px-2 py-1.5 text-sm" />
            </div>
            <div className="sm:col-span-2 md:col-span-3">
              <p className="text-xs font-medium mb-1">Prescrições específicas por dia (opcional — sobrepõem a base)</p>
              <p className="text-[11px] text-muted-foreground mb-2">Escreva <code>X</code> para {'"sem administração"'} nesse dia. Deixe vazio para usar a prescrição base.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2">
                {DAYS_META.map(d => (
                  <div key={d.key} className="flex items-center gap-2">
                    <label className="text-xs w-16 text-muted-foreground">{d.label.toLowerCase()}</label>
                    <input
                      data-testid={`novo-dia-${d.key}`}
                      value={(newTrat.prescricoes_dia || {})[d.key] ?? ''}
                      onChange={e => setNewTrat({ ...newTrat, prescricoes_dia: { ...(newTrat.prescricoes_dia || {}), [d.key]: e.target.value } })}
                      placeholder="(usa base)"
                      className="flex-1 rounded-lg border border-border bg-background px-2 py-1 text-xs" />
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={addTratamento} data-testid="btn-guardar-tratamento" className="text-sm bg-primary text-primary-foreground rounded-full px-4 py-1.5"><Save className="w-3.5 h-3.5 inline mr-1" />Guardar</button>
            <button onClick={() => setShowAddTrat(false)} className="text-sm bg-muted text-foreground rounded-full px-4 py-1.5">Cancelar</button>
          </div>
        </div>
      )}

      {/* Weekly grid table */}
      <div className="bg-card rounded-2xl border border-border overflow-hidden mb-8 print:border-0">
        {tratamentos.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">Nenhum tratamento ativo.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[11px] border-collapse" data-testid="tabela-semanal">
              <thead className="bg-muted/60">
                <tr>
                  <th className="border border-border px-1.5 py-1 w-10">Cão</th>
                  <th className="border border-border px-1.5 py-1 w-10">Gato</th>
                  <th className="border border-border px-1 py-1" colSpan={2}>Sexo<br /><span className="text-[10px] font-normal">M / F</span></th>
                  <th className="border border-border px-2 py-1 text-left">Características</th>
                  <th className="border border-border px-2 py-1">Peso</th>
                  <th className="border border-border px-2 py-1">Chip</th>
                  <th className="border border-border px-2 py-1 text-left">Local do Canil</th>
                  <th className="border border-border px-2 py-1 text-left">Motivo</th>
                  {dias.map(d => (
                    <th key={d.key} className="border border-border px-2 py-1 text-center whitespace-nowrap">
                      <div>{d.label}</div>
                      <div className="text-[10px] font-normal">{d.display}</div>
                    </th>
                  ))}
                  <th className="border border-border px-1 py-1 w-16 print:hidden">Ações</th>
                </tr>
              </thead>
              <tbody>
                {tratamentosVisiveis.map(t => {
                  const a = t.animal || {};
                  const isCao = (a.especie || '').toLowerCase() === 'canina';
                  const isGato = (a.especie || '').toLowerCase() === 'felina';
                  const sexo = (a.sexo || '').toLowerCase();
                  return (
                    <tr key={t.id} className="hover:bg-muted/20">
                      <td className="border border-border text-center">{isCao ? 'X' : ''}</td>
                      <td className="border border-border text-center">{isGato ? 'X' : ''}</td>
                      <td className="border border-border text-center w-6">{sexo.startsWith('m') ? 'X' : ''}</td>
                      <td className="border border-border text-center w-6">{sexo.startsWith('f') ? 'X' : ''}</td>
                      <td className="border border-border px-2 py-1 font-medium">{a.nome || '—'}</td>
                      <td className="border border-border px-2 py-1 text-center">{a.peso || ''}</td>
                      <td className="border border-border px-2 py-1 text-center">{a.chip || ''}</td>
                      <td className="border border-border px-2 py-1">{a.box || ''}</td>
                      <td className="border border-border px-2 py-1">{t.nome}{t.descricao && <div className="text-[10px] text-muted-foreground">{t.descricao}</div>}</td>
                      {dias.map(d => (
                        <td key={d.key} className="border border-border px-1.5 py-1 text-center align-middle text-[10.5px] whitespace-normal">
                          <span className={cellContent(t, d.iso, d.key) === 'X' ? 'text-muted-foreground' : ''}>
                            {cellContent(t, d.iso, d.key) || '—'}
                          </span>
                        </td>
                      ))}
                      <td className="border border-border text-center print:hidden">
                        <div className="flex justify-center gap-0.5">
                          <button data-testid={`edit-trat-${t.id}`} onClick={() => setEditing({
                            id: t.id, nome: t.nome, descricao: t.descricao, protocolo: t.protocolo,
                            data_inicio: t.data_inicio, data_fim: t.data_fim,
                            prescricoes_dia: { ...(t.prescricoes_dia || {}) },
                          })} title="Editar tratamento e prescrições por dia"
                            className="text-primary hover:bg-primary/10 p-1 rounded-full"><Edit className="w-3 h-3" /></button>
                          <button data-testid={`del-trat-${t.id}`} onClick={() => apagarTratamento(t)} title="Apagar"
                            className="text-destructive hover:bg-destructive/10 p-1 rounded-full"><Trash2 className="w-3 h-3" /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {tratamentos.length > PER_PAGE && (
        <div className="flex justify-center items-center gap-2 mb-6 print:hidden" data-testid="tratamentos-paginacao">
          <button onClick={() => setPagina(p => Math.max(0, p - 1))} disabled={paginaAtual === 0}
            className="p-2 rounded-full border border-border bg-card hover:bg-muted disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button>
          <span className="text-sm text-muted-foreground">Página {paginaAtual + 1} de {totalPaginas} · {tratamentos.length} tratamentos</span>
          <button onClick={() => setPagina(p => Math.min(totalPaginas - 1, p + 1))} disabled={paginaAtual >= totalPaginas - 1}
            className="p-2 rounded-full border border-border bg-card hover:bg-muted disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
        </div>
      )}

      {/* Edit treatment modal (per-day) */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-card rounded-2xl border border-border shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="edit-trat-modal">
            <div className="sticky top-0 bg-card border-b border-border p-4 flex items-center justify-between">
              <h3 className="font-heading font-bold text-lg">Editar tratamento</h3>
              <button onClick={() => setEditing(null)}><X className="w-5 h-5" /></button>
            </div>
            <div className="p-4 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><label className="text-xs block mb-1">Motivo / Nome</label>
                  <input value={editing.nome || ''} onChange={e => setEditing({ ...editing, nome: e.target.value })}
                    className="w-full rounded-lg border border-border bg-background px-2 py-1.5 text-sm" /></div>
                <div><label className="text-xs block mb-1">Protocolo</label>
                  <input value={editing.protocolo || ''} onChange={e => setEditing({ ...editing, protocolo: e.target.value })}
                    className="w-full rounded-lg border border-border bg-background px-2 py-1.5 text-sm" /></div>
                <div><label className="text-xs block mb-1">Data Início</label>
                  <DateInput value={editing.data_inicio} onChange={v => setEditing({ ...editing, data_inicio: v })} className="w-full" /></div>
                <div><label className="text-xs block mb-1">Data Fim</label>
                  <DateInput value={editing.data_fim} onChange={v => setEditing({ ...editing, data_fim: v })} className="w-full" /></div>
              </div>
              <div>
                <label className="text-xs block mb-1">Prescrição base (aplicada em todos os dias dentro do intervalo)</label>
                <input value={editing.descricao || ''} onChange={e => setEditing({ ...editing, descricao: e.target.value })}
                  placeholder="ex: 1 CP CONDROSATIVA"
                  className="w-full rounded-lg border border-border bg-background px-2 py-1.5 text-sm" />
              </div>

              <div>
                <p className="text-xs font-medium mb-1">Prescrições específicas por dia (opcional — sobrepõem a base)</p>
                <p className="text-[11px] text-muted-foreground mb-2">Escreva <code>X</code> para {'"sem administração"'} nesse dia. Deixe vazio para usar a prescrição base.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {DAYS_META.map(d => (
                    <div key={d.key} className="flex items-center gap-2">
                      <label className="text-xs w-20">{d.label.toLowerCase()}</label>
                      <input
                        data-testid={`edit-dia-${d.key}`}
                        value={(editing.prescricoes_dia || {})[d.key] ?? ''}
                        onChange={e => setEditing({ ...editing, prescricoes_dia: { ...(editing.prescricoes_dia || {}), [d.key]: e.target.value } })}
                        placeholder="(usa base)"
                        className="flex-1 rounded-lg border border-border bg-background px-2 py-1 text-xs" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="sticky bottom-0 bg-card border-t border-border p-3 flex gap-2 justify-end">
              <button onClick={() => setEditing(null)} className="px-4 py-2 rounded-full bg-muted text-foreground text-sm">Cancelar</button>
              <button onClick={guardarEdit} data-testid="save-edit-trat"
                className="px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-medium flex items-center gap-1"><Save className="w-4 h-4" />Guardar</button>
            </div>
          </div>
        </div>
      )}

      {/* Protocolos Terapêuticos */}
      <div className="bg-card rounded-2xl border border-border p-6 print:hidden">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-bold text-lg">Protocolos Terapêuticos / Informação Geral</h3>
          <button onClick={() => setShowAddProt(!showAddProt)} className="flex items-center gap-1 text-sm bg-primary text-primary-foreground rounded-full px-3 py-1.5"><Plus className="w-3.5 h-3.5" />Novo</button>
        </div>
        {showAddProt && (
          <div className="bg-muted/50 rounded-xl p-3 space-y-2 mb-4">
            <input placeholder="Título do protocolo" value={newProt.titulo} onChange={e => setNewProt({ ...newProt, titulo: e.target.value })} className="w-full rounded-lg border border-border bg-background px-2 py-1.5 text-sm" />
            <textarea placeholder="Conteúdo / instruções..." rows={4} value={newProt.conteudo} onChange={e => setNewProt({ ...newProt, conteudo: e.target.value })} className="w-full rounded-lg border border-border bg-background px-2 py-1.5 text-sm" />
            <button onClick={addProtocolo} className="text-xs bg-primary text-primary-foreground rounded-full px-3 py-1.5">Guardar</button>
          </div>
        )}
        {protocolos.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nenhum protocolo registado.</p>
        ) : (
          <div className="space-y-3">
            {protocolos.map(p => (
              <div key={p.id} className="bg-muted/30 rounded-xl p-4">
                <div className="flex justify-between items-start">
                  <h4 className="font-heading font-bold text-sm">{p.titulo}</h4>
                  <button onClick={() => deleteProtocolo(p.id)} className="text-destructive hover:bg-destructive/10 p-1 rounded"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
                <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">{p.conteudo}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      <p className="text-xs text-muted-foreground text-center mt-4 print:mt-2">Gerado em {new Date().toLocaleDateString('pt-PT')} — AnimaLog</p>
    </div>
  );
}
